// Native share-sheet helpers with graceful fallback to clipboard.
// Always returns a string outcome: "shared" | "copied" | "cancelled" | "failed".

import { toast } from "sonner";

function toastShareResult(outcome, opts = {}) {
  if (outcome === "shared") {
    toast.success(opts.sharedText || "Shared!");
  } else if (outcome === "copied") {
    toast.success(opts.copiedText || "Link copied to clipboard");
  } else if (outcome === "cancelled") {
    // No toast — user changed their mind, that's fine.
  } else if (outcome === "failed") {
    toast.error(opts.failedText || "Couldn't share — please try again");
  }
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  // Legacy fallback
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Trigger native share sheet with copy fallback.
 * @param {{title?: string, text?: string, url?: string, fallbackToast?: boolean, sharedText?: string, copiedText?: string, failedText?: string}} payload
 * @returns {Promise<"shared"|"copied"|"cancelled"|"failed">}
 */
export async function shareOrCopy(payload) {
  const { title, text, url, fallbackToast = true, ...toastOpts } = payload;
  const data = {};
  if (title) data.title = title;
  if (text) data.text = text;
  if (url) data.url = url;

  // Native share when available (mobile/PWA, modern desktop with Web Share)
  if (navigator.share && (!navigator.canShare || navigator.canShare(data))) {
    try {
      await navigator.share(data);
      if (fallbackToast) toastShareResult("shared", toastOpts);
      return "shared";
    } catch (err) {
      // AbortError = user cancelled, anything else falls through to copy
      if (err && err.name === "AbortError") {
        if (fallbackToast) toastShareResult("cancelled", toastOpts);
        return "cancelled";
      }
      // Fall through to clipboard fallback below
    }
  }

  // Clipboard fallback
  const linkOrText = url || text || title || "";
  if (!linkOrText) {
    if (fallbackToast) toastShareResult("failed", toastOpts);
    return "failed";
  }
  const copied = await copyToClipboard(linkOrText);
  const outcome = copied ? "copied" : "failed";
  if (fallbackToast) toastShareResult(outcome, toastOpts);
  return outcome;
}

/** Returns true when the browser supports the native share sheet (otherwise we copy). */
export function canNativeShare() {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}
